import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/styles';
import { Box, Grid, Paper, Typography, useMediaQuery } from '@material-ui/core';
import ConversationList from '../../components/ConversationList';
import ChatView from '../../components/ChatView';
import {
  clearCurrentConversation,
  conversationsLoadingSelector,
  conversationsSelector,
  currentConversationSelector,
  fetchConversations,
  fetchMessages,
  markMessagesAsRead,
  messagesLoadingSelector,
  messagesSelector,
  onlineUsersSelector,
  sendMessage,
  setCurrentConversation,
  typingUsersSelector
} from '../../redux/features/messages/messagesSlice';
import { useCurrentUserInfo } from '../../hooks/useCurrentUserInfo';
import websocketService from '../../services/websocketService';
import { getLocalStorageAuthentication } from '../../lib/auth';
import {
  trackConversationOpened,
  trackMessageSent,
  trackMessagesPageView,
  trackMessagesRead
} from '../../helpers/analytics';

const useStyles = makeStyles((theme) => ({
  root: {
    height: 'calc(100vh - 64px)',
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
      height: 'calc(100vh - 56px)'
    }
  },
  paper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  conversationListContainer: {
    height: '100%',
    borderRight: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('xs')]: {
      borderRight: 'none'
    }
  },
  chatViewContainer: {
    height: '100%'
  },
  header: {
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2)
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  },
  // Mobile-specific styles
  mobileHidden: {
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  mobileVisible: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'block'
    }
  }
}));

const Messages = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { user, userId } = useCurrentUserInfo();

  // State to track if we're showing the chat view on mobile
  const [showMobileChat, setShowMobileChat] = useState(false);

  const conversations = useSelector(conversationsSelector);
  const currentConversation = useSelector(currentConversationSelector);
  const messages = useSelector(messagesSelector);
  const conversationsLoading = useSelector(conversationsLoadingSelector);
  const messagesLoading = useSelector(messagesLoadingSelector);
  const onlineUsers = useSelector(onlineUsersSelector);

  // Debug logging
  console.log('📊 [Messages] Current state:', {
    conversationsCount: conversations.length,
    currentConversationId: currentConversation?.id,
    messagesCount: messages.length,
    conversationsLoading,
    messagesLoading
  });

  // Use optional chaining for selectors that depend on currentConversation
  const currentConversationId = currentConversation?.id;
  const typingUsers = useSelector(typingUsersSelector(currentConversationId));

  // Initialize WebSocket connection
  useEffect(() => {
    const localAuth = getLocalStorageAuthentication();
    if (localAuth && localAuth.token && !websocketService.isConnected()) {
      websocketService.connect(localAuth.token);
    }
  }, []);

  // Track page view when component mounts
  useEffect(() => {
    if (userId) {
      trackMessagesPageView(conversations.length);
    }
  }, [userId, conversations.length]); // Only track on initial mount

  useEffect(() => {
    if (userId && conversations.length === 0 && !conversationsLoading) {
      dispatch(fetchConversations());
    }
  }, [dispatch, userId, conversations.length, conversationsLoading]);

  // Fetch messages when conversation changes
  // The thunk handles preventing duplicate fetches internally
  useEffect(() => {
    if (currentConversationId) {
      dispatch(fetchMessages({ conversationId: currentConversationId }));

      // Always mark as read when viewing a conversation with unread messages
      if (currentConversation?.unreadCount > 0) {
        console.log(
          '📖 [Messages] Marking messages as read for:',
          currentConversationId
        );
        dispatch(markMessagesAsRead({ conversationId: currentConversationId }));

        // Track messages read
        trackMessagesRead(
          currentConversationId,
          currentConversation.unreadCount
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]); // Only depend on conversation ID change

  const handleConversationClick = (conversation) => {
    console.log('👆 [Messages] Clicked conversation:', conversation.id);
    dispatch(setCurrentConversation({ conversation }));

    // Show chat view on mobile
    if (isMobile) {
      setShowMobileChat(true);
    }

    // Track conversation opened
    const otherUserName =
      conversation.otherUserName || conversation.companyName || 'Unknown';
    trackConversationOpened(
      conversation.id,
      otherUserName,
      (conversation.unreadCount || 0) > 0
    );
  };

  const handleBackToConversations = () => {
    setShowMobileChat(false);
    dispatch(clearCurrentConversation());
  };

  const handleSendMessage = (content) => {
    if (currentConversation && content.trim()) {
      dispatch(
        sendMessage({
          conversationId: currentConversation.id,
          content: content.trim()
        })
      );

      // Track message sent
      trackMessageSent(currentConversation.id, content.trim().length);
    }
  };

  if (!user) {
    return (
      <Box className={classes.loadingContainer}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper} elevation={2}>
        {/* Hide header on mobile when viewing chat */}
        {!(isMobile && showMobileChat) && (
          <Box className={classes.header}>
            <Typography variant="h4">Messages</Typography>
          </Box>
        )}
        <Grid container style={{ flex: 1, overflow: 'hidden' }}>
          {/* Conversation List - Hidden on mobile when viewing chat */}
          {!(isMobile && showMobileChat) && (
            <Grid
              item
              xs={12}
              sm={4}
              md={4}
              className={classes.conversationListContainer}>
              <ConversationList
                conversations={conversations}
                currentConversation={currentConversation}
                onConversationClick={handleConversationClick}
                loading={conversationsLoading}
                onlineUsers={onlineUsers}
                currentUserId={userId}
              />
            </Grid>
          )}

          {/* Chat View - Full width on mobile, hidden when no conversation selected */}
          {(!isMobile || showMobileChat) && (
            <Grid
              item
              xs={12}
              sm={8}
              md={8}
              className={classes.chatViewContainer}>
              <ChatView
                conversation={currentConversation}
                messages={messages}
                currentUserId={userId}
                loading={messagesLoading}
                onSendMessage={handleSendMessage}
                typingUsers={typingUsers}
                isOtherUserOnline={
                  currentConversation
                    ? !!onlineUsers[
                        currentConversation.companyUserId === userId
                          ? currentConversation.candidateUserId
                          : currentConversation.companyUserId
                      ]
                    : false
                }
                onBack={isMobile ? handleBackToConversations : null}
                isMobile={isMobile}
              />
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default Messages;
